// src/store/colorPaletteStore.js
import { defineStore } from 'pinia'
import { shallowRef, computed, readonly } from 'vue'
import { groupBy } from 'lodash-es'
import { logInfo, logError, logDebug } from '@/utils/logHelper'
import colorPaletteApi from '@/common/api/cache/colorPaletteApi'

/* =========================================================
 * 상수 정의
 * ======================================================= */
const STORE_NAME = 'colorPalette'
const LOG_TAG = `[${STORE_NAME}Store]`

const ERRORS = Object.freeze({
  FETCH_FAILED: '색상 데이터 조회 실패',
  RELOAD_FAILED: '색상 데이터 리로드 실패',
})

// Material Natural Color 기본값
const MATERIAL_NATURAL_COLOR = Object.freeze({
  materialCode: '',
  colorCode: 'Material Natural Color',
  colorCodeCount: 0,
  colorName: 'Material Natural Color',
})

/* =========================================================
 * Pinia Store 정의
 * ======================================================= */
export const useColorPaletteStore = defineStore(STORE_NAME, () => {
  /* =========================================================
   * 1) State
   * ======================================================= */
  // 서버에서 materialCode, colorCode, colorCodeCount 를 정확히 내려준다고 가정
  const colorData = shallowRef([]) // { materialCode, colorCode, colorCodeCount }[]
  const loaded = shallowRef(false)
  const loading = shallowRef(false)

  // Material Natural Color 데이터
  const materialNaturalColorData = shallowRef({ ...MATERIAL_NATURAL_COLOR })

  let fetchPromise = null

  /* =========================================================
   * 2) Computed (Getters)
   * ======================================================= */
  /**
   * 전체 색상 리스트 (읽기 전용)
   */
  const colorList = computed(() => colorData.value)

  /**
   * materialCode로 그룹화된 Map
   */
  const colorsByMaterial = computed(() => {
    const grouped = groupBy(colorData.value, 'materialCode')
    return new Map(Object.entries(grouped))
  })

  /**
   * 특정 materialCode의 색상 리스트 반환 함수
   */
  const getColorsByMaterialCode = computed(() => {
  return (materialCode) => {
    const colors = colorsByMaterial.value.get(materialCode) || []

    const {
      materialCode: naturalMaterialCode,
      colorCode,
      colorCodeCount,
      colorName,
    } = materialNaturalColorData.value

    if (
      naturalMaterialCode &&
      naturalMaterialCode === materialCode &&
      colorCode &&
      colorCode.trim()
    ) {
      return [
        ...colors.map((c) => ({
          colorCode: c.colorCode,
          colorCodeCount: c.colorCodeCount,
          colorName: c.colorName || "",          // ✅ 색상명 유지
        })),
        {
          colorCode,
          colorCodeCount,
          colorName: colorName || "Material Natural Color", // 필요시 변경
        },
      ]
    }

    return colors.map((c) => ({
      colorCode: c.colorCode,
      colorCodeCount: c.colorCodeCount,
      colorName: c.colorName || "",              // ✅ 여기
    }))
  }
})


  /**
   * Material Natural Color 정보
   */
  const getMaterialNaturalColor = computed(() => materialNaturalColorData.value)

  /**
   * 전체 색상 개수
   */
  const colorCount = computed(() => colorData.value.length)

  /**
   * 등록된 materialCode 개수
   */
  const materialCount = computed(() => colorsByMaterial.value.size)

  /**
   * 로딩 상태
   */
  const isLoading = computed(() => loading.value)
  const isLoaded = computed(() => loaded.value)

  /* =========================================================
   * 3) Helpers
   * ======================================================= */
  function updateStoreData(rawData) {
    // 서버가 배열을 반환한다고 가정하고 그대로 사용
    const array = Array.isArray(rawData) ? rawData : []
    colorData.value = array
    loaded.value = true

    logDebug(
      `${LOG_TAG} 스토어 업데이트: ${array.length}개 색상, ${materialCount.value}개 소재`,
      array,
    )
  }

  function resetLoadingState() {
    loading.value = false
    fetchPromise = null
  }

  /* =========================================================
   * 4) Actions
   * ======================================================= */
  async function fetchColorData() {
    if (loaded.value) {
      logDebug(`${LOG_TAG} 캐시 사용 (${colorCount.value}개)`)
      return
    }

    if (fetchPromise) {
      logDebug(`${LOG_TAG} 진행 중인 요청 대기`)
      return fetchPromise
    }

    loading.value = true

    fetchPromise = (async () => {
      try {
        logInfo(`${LOG_TAG} 색상 데이터 조회 시작`)
        const response = await colorPaletteApi.getAllColors()
        updateStoreData(response)
        logInfo(
          `${LOG_TAG} 로드 완료: ${materialCount.value}개 소재, ${colorCount.value}개 색상`,
        )
      } catch (error) {
        logError(`${LOG_TAG} ${ERRORS.FETCH_FAILED}:`, error)
        colorData.value = []
        loaded.value = true
        throw error
      } finally {
        resetLoadingState()
      }
    })()

    return fetchPromise
  }

  async function reloadColorData() {
    loading.value = true

    try {
      logInfo(`${LOG_TAG} 서버 캐시 리로드 요청`)

      await colorPaletteApi.reloadColorPalette()
      logInfo(`${LOG_TAG} 서버 캐시 리로드 완료`)

      loaded.value = false
      fetchPromise = null
      colorData.value = []

      await fetchColorData()
    } catch (error) {
      logError(`${LOG_TAG} ${ERRORS.RELOAD_FAILED}:`, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  function setMaterialNaturalColor(config) {
    if (!config || typeof config !== 'object') {
      logError(`${LOG_TAG} Invalid Material Natural Color config:`, config)
      return
    }

    materialNaturalColorData.value = {
      materialCode: String(config.materialCode ?? ''),
      colorCode: String(config.colorCode ?? MATERIAL_NATURAL_COLOR.colorCode),
      colorCodeCount: Number(config.colorCodeCount ?? 0),
    }

    logDebug(`${LOG_TAG} Material Natural Color 업데이트:`, 
      materialNaturalColorData.value)
  }

  function reset() {
    colorData.value = []
    loaded.value = false
    loading.value = false
    materialNaturalColorData.value = { ...MATERIAL_NATURAL_COLOR }
    fetchPromise = null

    logDebug(`${LOG_TAG} 스토어 초기화`)
  }

  /* =========================================================
   * 5) Return
   * ======================================================= */
  return {
    // State (readonly)
    colorData: readonly(colorData),
    materialNaturalColorData: readonly(materialNaturalColorData),

    // Getters
    colorList,
    colorsByMaterial,
    getColorsByMaterialCode,
    getMaterialNaturalColor,
    colorCount,
    materialCount,
    isLoading,
    isLoaded,

    // Actions
    fetchColorData,
    reloadColorData,
    setMaterialNaturalColor,
    reset,
  }
})
